import React, { useCallback, useMemo } from 'react';
import { useTheme } from '@mui/material';
import {
    Alert,
    Divider,
    INLINE_VARIANT,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Illustration,
    makeStyles,
    useMediaQuery
} from '@ellucian/react-design-system/core';
import { FormattedMessage, useIntl } from 'react-intl';
import useApplicationForm from './useApplicationForm';
import { routes } from '../Helpers/Configuration';
import { spacing50, spacing40, spacing60 } from '@ellucian/react-design-system/core/styles/tokens';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { createSelector } from '@reduxjs/toolkit';
import SupplementalItemsTableRow from './SupplementalItemsTableRow';
import SupplementalItemsMobileRow from './SupplementalItemsMobileRow';
import HtmlFormattedMessage from '../HtmlFormattedMessage/HtmlFormattedMessage';

const useStyles = makeStyles(() => ({
    root: {
        display: 'grid',
        gridTemplateRows: '1fr',
        gridGap: spacing50,
    },
    pageDivider: {
        margin: [0, '!important'],
    },
    noSupplementalItems: {
        display: 'grid',
        gridGap: spacing40,
        justifyItems: 'center',
        marginBottom: spacing60,
    },
    noSupplementalItemsText: {
        textAlign: 'center',
    },
    removeQuillParagraphWrapperMargin: {
        '& > p': {
            marginBottom: 0,
            marginTop: 0,
        },
    },
}));

const SupplementalItemsChecklist = () => {
    const classes = useStyles();
    const { formatMessage } = useIntl();
    const theme = useTheme();
    const history = useHistory();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { template, selectedLocale } = useApplicationForm();

    const handleFormNavigate = useCallback((suppForm) => {
        if (suppForm?.supplementalFormId) {
            history.push(routes.applicationFormSupplementalForm.replace(':id', suppForm.supplementalFormId));
        }
    }, [history]);

    const selectSortedItems = useMemo(() => {
        const localeComparator = (a, b) => {
            let nameA = formatMessage({ id: a.nameId });
            let nameB = formatMessage({ id: b.nameId });
            let requiredA = a.required;
            let requiredB = b.required;
            return requiredB - requiredA || nameA.localeCompare(nameB, selectedLocale);
        };
        return createSelector(
            state => state.application.supplementalItems || [],
            value => {
                return [...value].sort(localeComparator);
            }
        );
    }, [formatMessage, selectedLocale]);

    const items = useSelector(selectSortedItems);


    return (
        <div className={classes.root}>
            <Typography variant="h2">
                <FormattedMessage id="supplementalItemsChecklist" />
            </Typography>
            <Divider className={classes.pageDivider} />
            {template.supplementalItems.completeDescriptionId &&
                <Alert
                    alertType="info"
                    open={true}
                    transitionDuration={0}
                    userDismissable={false}
                    variant={INLINE_VARIANT}
                >
                    <div className={classes.removeQuillParagraphWrapperMargin}>
                        <HtmlFormattedMessage descriptor={{ id: template.supplementalItems.completeDescriptionId }} />
                    </div>
                </Alert>
            }
            {items.length <= 0 &&
                <div className={classes.noSupplementalItems}>
                    <Illustration name="no-tasks" />
                    <Typography variant="h3">
                        {template.supplementalItems.noItemMessageId ?
                            <HtmlFormattedMessage descriptor={{ id: template.supplementalItems.noItemMessageId }} />
                            :
                            <FormattedMessage id="noSupplementalItemsChecklistTitle" />
                        }
                    </Typography>
                    <div className={classes.noSupplementalItemsText}>
                        <Typography>
                            {template.supplementalItems.noItemDescriptionId ?
                                <HtmlFormattedMessage descriptor={{ id: template.supplementalItems.noItemDescriptionId }} />
                                :
                                <FormattedMessage id="noSupplementalItemsChecklistBody" />
                            }
                        </Typography>
                    </div>
                </div>
            }
            {items.length > 0 && !isMobile &&
                <Table className={classes.table}>
                    <TableHead>
                        <TableRow>
                            <TableCell><FormattedMessage id="supplementalItemListName" /></TableCell>
                            <TableCell><FormattedMessage id="supplementalItemListDescription" /></TableCell>
                            <TableCell><FormattedMessage id="supplementalItemListRequired" /></TableCell>
                            <TableCell><FormattedMessage id="supplementalItemListStatus" /></TableCell>
                            <TableCell><FormattedMessage id="supplementalItemListDateReceived" /></TableCell>
                            <TableCell><FormattedMessage id="supplementalItemListActions" /></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map(it => {
                            return (
                                <SupplementalItemsTableRow
                                    key={it.name}
                                    item={it}
                                    handleFormNavigate={handleFormNavigate}
                                />
                            );
                        })}
                    </TableBody>
                </Table>
            }
            {items.length > 0 && isMobile &&
                <Typography component="div" id="suppMobile">
                    {items.map(it => {
                        return (
                            <SupplementalItemsMobileRow
                                key={it.name}
                                item={it}
                                handleFormNavigate={handleFormNavigate}
                            />
                        );
                    })}
                </Typography>
            }
        </div>
    );
};
export default SupplementalItemsChecklist;
